/**
 * Agent key as it appears in the borrower-portal endpoint path.
 */
export const MERCHANT_INTERVIEW_AGENT_KEY = 'merchant-interview'

/**
 * Opening copy, keyed by the path template the borrower resolved to.
 * Rendered client-side — no model call.
 * `anchored` takes the anchor phrase ("around $65K for equipment"); `generic` is the no-facts fallback.
 */
const INVITATIONS = {
  merchant_interview_options_unseen: {
    generic:
      "I'm Lendio's AI assistant. While we're matching you with offers, I have a few quick "
      + 'questions that will help us find the best options for your business. Do you have a moment?',
    anchored: (anchor) =>
      `I'm Lendio's AI assistant. I see you're looking for ${anchor} — while we're matching you `
      + 'with offers, I have a few quick questions to help us find the best options for your '
      + 'business. Do you have a moment?',
  },
  merchant_interview_options_seen: {
    generic:
      "I'm Lendio's AI assistant. You've got options to look at — I have a few quick questions "
      + 'so we can be sure the details behind them are right. Do you have a moment?',
    anchored: (anchor) =>
      `I'm Lendio's AI assistant. I see you're looking for ${anchor}, and you've got options to `
      + 'look at — I have a few quick questions so we can be sure the details behind them are '
      + 'right. Do you have a moment?',
  },
}

// loanPurpose values w/ a phrase that reads after "for"; 'other' deliberately unmapped
const USE_OF_FUNDS_PHRASES = {
  expansion: 'expansion',
  workingCapital: 'working capital',
  payroll: 'payroll',
  purchaseABusiness: 'purchasing a business',
  equipment: 'equipment',
  realEstate: 'real estate',
  startABusiness: 'starting a business',
  financeAccountsReceivable: 'financing accounts receivable',
}

function formatAmount(value) {
  const amount = Number(value)

  if (!Number.isFinite(amount) || amount <= 0) return null
  if (amount >= 1_000_000) {
    return amount % 100_000 === 0 ? `$${amount / 1_000_000}M` : `$${amount.toLocaleString('en-US')}`
  }
  if (amount >= 1000 && amount % 1000 === 0) return `$${amount / 1000}K`

  return `$${amount.toLocaleString('en-US')}`
}

/**
 * "around $65K for equipment" from the eligibility anchor facts; empty when no fact maps.
 * useOfFunds is the raw loanPurpose value (comma-separated when multi) — only mapped
 * values interpolate; unmapped ones drop silently.
 */
function anchorPhrase({ amountSeeking, useOfFunds } = {}) {
  const amount = formatAmount(amountSeeking)
  const purposeKey = String(useOfFunds ?? '')
    .split(',')
    .map((value) => value.trim())
    .find((value) => Object.hasOwn(USE_OF_FUNDS_PHRASES, value))
  const purpose = purposeKey ? USE_OF_FUNDS_PHRASES[purposeKey] : null

  if (amount && purpose) return `around ${amount} for ${purpose}`
  if (amount) return `around ${amount}`
  if (purpose) return `funding for ${purpose}`

  return ''
}

/**
 * @param {?string} templateAlias
 * @param {?{firstName: ?string, amountSeeking: ?number, useOfFunds: ?string}} anchorFacts
 *   greets by name and anchors the copy on amount/purpose when facts are present
 *
 * @returns {string} empty when the alias is unknown
 */
export function merchantInterviewInvitation(templateAlias, anchorFacts = null) {
  const invitation = Object.hasOwn(INVITATIONS, templateAlias ?? '') ? INVITATIONS[templateAlias] : null

  if (!invitation) return ''

  const name = typeof anchorFacts?.firstName === 'string' ? anchorFacts.firstName.trim() : ''
  const anchor = anchorPhrase(anchorFacts ?? {})
  const body = anchor ? invitation.anchored(anchor) : invitation.generic

  return `${name ? `Howdy ${name}!` : 'Howdy!'} ${body}`
}
